import React, { useEffect, useState } from 'react';
import { useAdminTheme } from '../context/AdminThemeContext';
import { Edit, Trash2, Search, Loader2, User, AlertTriangle, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Notification from '../../Component/Notification';
import { useAuth } from '../../context/AuthContext'; // Using context for token

const StudentList: React.FC = () => {
  const { colors } = useAdminTheme();
  const { token } = useAuth(); // Needed for API calls
  const navigate = useNavigate();
  
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Notification State
  const [notify, setNotify] = useState<{ isOpen: boolean; message: string; type: 'success' | 'error' }>({
    isOpen: false, message: '', type: 'success'
  });

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      // const response = await fetch('http://localhost:2025/api/admin/students', {
      const response = await fetch('https://www.projectinfotech.in/api/admin/students', {
        headers: { 'Authorization': `Bearer ${token}` } // Send Token
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(data);
      }
    } catch (error) {
      console.error("Failed to fetch students");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if(!window.confirm("Are you sure you want to delete this student permanently?")) return;
    
    try {
        // await fetch(`http://localhost:2025/api/admin/students/${id}`, { 
        await fetch(`https://www.projectinfotech.in/api/admin/students/${id}`, { 
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
        });
        setStudents(prev => prev.filter(s => s.id !== id));
        setNotify({ isOpen: true, message: 'Student deleted successfully', type: 'success' });
    } catch (error) {
        setNotify({ isOpen: true, message: 'Failed to delete student', type: 'error' });
    }
  };

  // --- NEW: Handle Set Key ---
  const handleSetKey = async (student: any) => {
    const newKey = prompt(`Enter Access Key for ${student.name} (e.g., SECRET123):`, student.accessKey || "");
    
    if (newKey !== null) { // If not cancelled
        try {
            const res = await fetch('https://www.projectinfotech.in/api/notes/set-key', {
            // const res = await fetch('http://localhost:2025/api/notes/set-key', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ userId: student.id.toString(), key: newKey })
            });

            if (res.ok) {
                setNotify({ isOpen: true, message: `Key updated for ${student.name}`, type: 'success' });
                // Update local state to reflect change without refetch
                setStudents(prev => prev.map(s => s.id === student.id ? { ...s, accessKey: newKey } : s));
            } else {
                setNotify({ isOpen: true, message: 'Failed to update key', type: 'error' });
            }
        } catch (error) {
            setNotify({ isOpen: true, message: 'Server error', type: 'error' });
        }
    }
  };

  const filteredStudents = students.filter(s => 
    s.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.mobile?.includes(searchTerm)
  );

  return (
    <div className="animate-fade-in-up">
      {/* Notification Popup */}
      <Notification 
        isOpen={notify.isOpen} 
        message={notify.message} 
        type={notify.type} 
        onClose={() => setNotify({ ...notify, isOpen: false })} 
      />

      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <h2 className={`text-2xl font-bold ${colors.navbarText}`}>Registered Users</h2>
        <div className="relative w-full md:w-auto">
             <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
             <input 
               type="text" placeholder="Search users..." 
               value={searchTerm}
               onChange={(e) => setSearchTerm(e.target.value)}
               className={`pl-10 pr-4 py-2 rounded-lg border focus:outline-none w-full md:w-64 ${colors.inputBg} ${colors.borderColor}`} 
             />
        </div>
      </div>

      <div className={`rounded-xl shadow-sm border overflow-hidden ${colors.cardBg} ${colors.borderColor}`}>
        {loading ? (
           <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-red-600" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
                <thead className={`${colors.tableHeaderBg}`}>
                <tr>
                    <th className="p-4 font-semibold text-sm">ID</th>
                    <th className="p-4 font-semibold text-sm">Name</th>
                    <th className="p-4 font-semibold text-sm">Email</th>
                    <th className="p-4 font-semibold text-sm">Mobile</th>
                    <th className="p-4 font-semibold text-sm">Access Key</th> 
                    <th className="p-4 font-semibold text-sm text-right">Actions</th>
                </tr>
                </thead>
                <tbody className={`${colors.cardText}`}>
                {filteredStudents.length > 0 ? (
                    filteredStudents.map((student) => (
                    <tr key={student.id} className={`border-t ${colors.borderColor} hover:opacity-80 transition-opacity`}>
                        <td className="p-4 text-sm opacity-70">#{student.id}</td>
                        <td className="p-4 font-bold flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-xs">
                                <User size={14} />
                            </div>
                            {student.name}
                        </td>
                        <td className="p-4 opacity-80 text-sm">{student.email}</td>
                        <td className="p-4 text-sm">{student.mobile || "N/A"}</td>
                        
                        {/* Access Key Column */}
                        <td className="p-4">
                            {student.accessKey ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-mono font-bold border border-green-200">
                                    <Key size={10} /> {student.accessKey}
                                </span>
                            ) : (
                                <span className="text-xs text-gray-400 italic">Not Set</span>
                            )}
                        </td>

                        <td className="p-4 text-right flex justify-end gap-2">
                        {/* Set Key Button */}
                        <button 
                            onClick={() => handleSetKey(student)}
                            className="p-2 rounded-lg bg-yellow-100 text-yellow-700 hover:bg-yellow-200 transition"
                            title="Set Download Key"
                        >
                            <Key size={16} />
                        </button>

                        <button 
                            onClick={() => navigate(`/admin/students/edit/${student.id}`)}
                            className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition"
                            title="Edit User"
                        >
                            <Edit size={16} />
                        </button>
                        <button 
                            onClick={() => handleDelete(student.id)}
                            className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition"
                            title="Delete User"
                        >
                            <Trash2 size={16} />
                        </button>
                        </td>
                    </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan={6} className="p-10 text-center opacity-60 flex flex-col items-center justify-center w-full">
                            <AlertTriangle size={32} className="mb-2 text-yellow-500" />
                            No users found.
                        </td>
                    </tr>
                )}
                </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentList;